/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Hotel } from '@app/hotels/hotels/hotel.model';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CitiesSelectComponent } from '@app/shared/components/cities-select/cities-select.component';
import { NgxGoogleAddressAutocompleteDirective } from 'ngx-google-address-autocomplete';
import { HotelsService } from '@app/hotels/hotels/hotels.service';
import { ApiQuery } from '@app/core/http/api-query';
import { ContactPersonComponent } from '@app/shared/components/contact-person/contact-person.component';
import { ContactComponent } from '@app/shared/components/contact/contact.component';
import { VenueDistanceComponent } from '@app/shared/components/venue-distance/venue-distance.component';
import { Logger } from '@app/core';
import { Venue } from '@app/settings/venues/venue.model';
import { FacilitiesService } from '@app/settings/facilities/facilities.service';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { Facility } from '@app/settings/facilities/facilities.model';
import { LaravelModelsEnum, Model } from '@app/shared/models/model.model';
import { City } from '@app/settings/cities/cities.model';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CitiesService } from '@app/settings/cities/cities.service';
import { ConfirmService } from '@app/shared/services/confirm.service';
const log = new Logger('HotelsFormComponent');

@Component({
  selector: 'app-hotels-form',
  templateUrl: './hotels-form.component.html',
  styleUrls: ['./hotels-form.component.scss'],
})
export class HotelsFormComponent implements OnInit {
  @Input() hotel: Hotel = null;
  form: FormGroup;
  @ViewChild('citySelect', { static: true }) citySelect: CitiesSelectComponent;
  @ViewChild(ContactComponent) contactComponent: ContactComponent;
  @ViewChild(ContactPersonComponent) contactPersonsComponent: ContactPersonComponent;
  LaravelModelsEnum = LaravelModelsEnum;
  city: Observable<City[]>;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private hotelService: HotelsService,
    private facilitiesService: FacilitiesService,
    private cityService: CitiesService,
    private confirmService: ConfirmService
  ) {}

  ngOnInit() {
    this.createForm();
    // this.generateGPOptions();
    this.patchForm();
  }

  /**
   * При избиране на адрес от google places
   * @param address Обекта, който идва от google places
   */
  public handleAddressChange(address: any) {
    this.form.get('address')?.setValue(address.name);
    this.form.get('lat')?.setValue(Number(address.lat));
    this.form.get('long')?.setValue(Number(address.lng));

    if (address.address_components && Array.isArray(address.address_components)) {
      let selectedCity = '';
      let selectedCityNames = '';
      if (this.citySelect.selectedCity === null) {
        selectedCity = this.form.get('city').value.name;
      } else {
        selectedCity = this.citySelect.selectedCity.name; // The selected city from the city picker
        selectedCityNames = this.citySelect.selectedCity.alternatenames ?? '';
      }
      const addressComponents = address.address_components;

      const cityComponent = addressComponents.find(
        (c) => c.types.includes('locality') || c.types.includes('postal_town')
      );
      const foundCity = cityComponent?.long_name || '';
      const process = (selectedCity: string, selectedCityNames: string) => {
        if (
          foundCity.toLowerCase() !== selectedCity.toLowerCase() &&
          selectedCityNames.toLowerCase().indexOf(foundCity.toLowerCase()) == -1
        ) {
          console.error(`Selected address is not in ${selectedCity}`);
          this.form.get('address')?.reset();
          this.form.get('lat')?.reset();
          this.form.get('long')?.reset();
          alert(`Please select an address in ${selectedCity}`);
        }
      };

      if (this.citySelect.selectedCity === null) {
        const selectedCityID = this.form.get('city_id')?.value;
        if (selectedCityID) {
          this.loadCityById(selectedCityID).subscribe((city: City) => {
            const selectedCity = city.name;
            const selectedCityNames = city.alternatenames ?? '';
            process(selectedCity, selectedCityNames);
          });
        } else {
          alert('Please select a city first.');
        }
      } else {
        const selectedCity = this.citySelect.selectedCity.name;
        const selectedCityNames = this.citySelect.selectedCity.alternatenames ?? '';
        process(selectedCity, selectedCityNames);
      }
    } else {
      alert('Could not retrieve city information. Please select a valid address.');
    }
  }

  loadCityById(id: number): Observable<Model> {
    const query = new ApiQuery().addInclude('country');
    return this.cityService.show(id, query);
  }

  save() {
    const checks = [];

    if (this.contactComponent) {
      checks.push(this.contactComponent.checkAllContactsExists());
    }

    if (this.contactPersonsComponent) {
      checks.push(this.contactPersonsComponent.checkAllContactsExists());
    }

    if (checks.length > 0) {
      forkJoin(checks)
        .pipe(map((results) => results.some((r) => !!r)))
        .subscribe((hasDuplicates) => {
          if (hasDuplicates) {
            this.confirmService.confirm('Duplicates found. Save anyway?', 'Duplicate contacts').subscribe((ok) => {
              if (ok) {
                this.performSave();
              }
            });
          } else {
            this.performSave();
          }
        });
    } else {
      this.performSave();
    }
  }

  private performSave() {
    if (this.hotel && this.hotel.id) {
      const hotel: Hotel = this.hotel.deserialize(this.formatPost());
      this.hotelService.update(hotel).subscribe((res) => {
        this.modal.close(res);
      });
    } else {
      const hotel: Hotel = new Hotel().deserialize(this.formatPost());
      this.hotelService.store(hotel).subscribe((res) => {
        this.modal.close(res);
      });
    }
  }

  /**
   * При преместване на пина на картата пълни полетата lat и long
   * @param event обекта който връща google maps след като пинът бъде преместен
   */
  selectLocation(event: any) {
    if (event && event.latLng) {
      const { lat, lng } = event.latLng.toJSON();
      this.form.get('lat')?.setValue(lat);
      this.form.get('long')?.setValue(lng);
      console.log('Location updated:', lat, lng);
    } else {
      console.warn('Event structure unexpected:', event);
    }
  }

  /**
   * Парсва стрингосани числа
   * @param number Подава се стрингосано число или число
   */
  number(number: any): number {
    return Number(number);
  }

  test(form: any) {
    log.debug(form);
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      id: [null],
      name: ['', [Validators.required]],
      type_id: [null, [Validators.required]],
      logo: [null],
      new_logo: [null],
      stars: [null],
      rooms_count: [null],
      bank_accounts: new FormArray([]),
      facilities: new FormArray([]),
      contact_persons: new FormArray([]),
      contacts: new FormArray([]),
      venues: new FormArray([]),
      address: ['', [Validators.required]],
      distance_to_center: [''],
      distance_to_airport: [''],
      distance_to_tr_point: [''],
      distance_to_tr_point_name: [''],
      lat: [0, [Validators.required]],
      long: [0, [Validators.required]],
      city_id: [null, [Validators.required]],
      active: [true],
      note: [null],
      gallery_count: [null],
      vat_number: [null],
      show_to_web: [false],
      tags: [],
    });

    const query = new ApiQuery().addFilter('active', true).setLimit(9999);
    this.facilitiesService.index(query).subscribe((response: PaginatedResponse) => {
      const facilitiesFormArray: FormArray = new FormArray([]);

      response.data.forEach((facility: Facility) => {
        const facilityFormGroup = new FormGroup({
          id: new FormControl(facility.id),
          name: new FormControl(facility.name),
          description: new FormControl(null),
        });
        facilitiesFormArray.push(facilityFormGroup);
      });

      this.form.setControl('facilities', facilitiesFormArray);
    });
  }

  private patchForm() {
    /*
     * гетва hotel-а и го пълни
     */
    if (this.hotel && this.hotel.id) {
      /*
       * Гетва hotel-a от сървиса по ID
       * Тук се подават като параметри всички includes(relations), които са нужни
       */
      const apiQuery: ApiQuery = new ApiQuery()
        .setLimit(0)
        .addIncludes(
          'bank_accounts',
          'contacts',
          'venues',
          'contact_persons.contacts',
          'contact_persons.event_group',
          'city.country',
          'facilities',
          'logo',
          'gallery_count',
          'tags'
        );
      this.hotelService.show(this.hotel.id, apiQuery).subscribe((hotel: any) => {
        /*
         *  Обикаля се взетия от сървиса hotel и се нагласят някои неща, за да може да се patch-не
         */
        if (hotel['contacts'].length > 0) {
          this.form.setControl('contacts', ContactComponent.createMany(hotel['contacts']));
        }

        if (hotel['contact_persons'].length > 0) {
          this.form.setControl('contact_persons', ContactPersonComponent.createMany(hotel['contact_persons'], true));
        }

        if (hotel['venues'].length > 0) {
          this.form.setControl('venues', VenueDistanceComponent.createMany(hotel['venues']));
        }

        hotel['venues'] = hotel['venues'].map((venue: Venue) => {
          return { ...venue, ...{ distance: venue.pivot.distance, note: venue.pivot.note } };
        });

        setTimeout(() => {
          log.debug('patch', hotel);

          const facilities = hotel.facilities;
          delete hotel.facilities;
          this.form.patchValue(hotel);

          if (hotel.gallery_count == 0) {
            this.form.controls['show_to_web'].disable();
          }

          facilities.forEach((facility: Facility) => {
            // @ts-ignore
            this.form.get('facilities').controls.forEach((facilityControl) => {
              log.debug('facilityControl', facilityControl.get('id').value);
              if (facilityControl.get('id').value === facility.id) {
                facilityControl.get('description').patchValue(facility.pivot.description);
              }
            });
          });
        });
      });
    } else if (this.hotel) {
      this.form.patchValue(this.hotel);
    }
  }

  get countryCode() {
    return this.hotel ? this.hotel.city.country.code : '';
  }

  private formatPost() {
    this.form.get('venues').value.forEach((venue: any, index: number) => {
      if (!venue.id && !venue.distance && !venue.note) {
        this.form.get('venues').value.splice(index, 1);
      }
    });

    this.form.get('bank_accounts').value.forEach((acc: any, index: number) => {
      if (!acc.name && !acc.iban && !acc.bic) {
        this.form.get('bank_accounts').value.splice(index, 1);
      }
    });

    return this.form.value;
  }

  // https://maps.googleapis.com/maps/api/
  // distancematrix/json?origins=42.688481,%2023.269978&destinations=42.686853,%2023.344659&key=AIzaSyDW9r7T-KbqBXRPVzl-4Kl0x2mgVoySXY4
}
